/** @file facet.setEntry: merges fields into one facet entry (table and drawn angles have their own commands). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import type { Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { FACET_ENTRY_SHAPE } from '@zilo/pose3d-format/validate/shapes/facetEntryShape';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { pivotWarnings } from '../drawable/pivotWarnings';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { CommandInput } from '../types/command';
import { readInput } from '../validate/readInput';
import { entryInverse } from './entryInverse';
import { findEntry } from './findEntry';

const OWN_COMMANDS = new Set(['yaw', 'drawnAzimuths', 'drawnScreenAngles']);
const FIELDS: Shape = {
  required: {},
  optional: Object.fromEntries(Object.entries({ ...FACET_ENTRY_SHAPE.required, ...FACET_ENTRY_SHAPE.optional }).filter(([k]) => !OWN_COMMANDS.has(k))),
};

type Fields = CommandInput['facet.setEntry']['fields'];

function checkFields(fields: Fields, entry: FacetEntry): void {
  if (fields.slots !== undefined && Object.keys(fields.slots).length > 0) {
    throwError(ERROR_CODES.VALIDATION, { slots: fields.slots }, { path: '/input/fields/slots', hint: 'only {} (reset); fill tiles with slot.fill' });
  }
  const filled = fields.slots === undefined && Object.keys(entry.slots).length > 0;
  if (fields.size !== undefined && filled && (fields.size[0] !== entry.size[0] || fields.size[1] !== entry.size[1])) {
    throwError(ERROR_CODES.VALIDATION, { size: fields.size }, { path: '/input/fields/size', hint: 'the entry holds images of the old size; reset slots: {} too' });
  }
}

/**
 * `facet.setEntry { drawable, entry, fields }` → `{}`: `slots` only as `{}`; no size change while slots hold images.
 * Inverse: `facet.setEntry` with the previous values when every field existed (and no slots were reset), else E.
 */
export const facetSetEntryCommand: CommandDef<'facet.setEntry'> = {
  name: 'facet.setEntry',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['drawable', 'entry', 'fields']);
    return { drawable: r.str('drawable'), entry: r.int('entry', { min: 0 }), fields: expectShape(r.raw('fields'), r.at('fields'), FIELDS) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const { drawableIndex, impostor, entry } = findEntry(draft.puppet, input.drawable, input.entry);
    checkFields(input.fields, entry);
    const prev = entry as Record<string, unknown>;
    const keys = Object.keys(input.fields);
    const cheap = keys.every((k) => prev[k] !== undefined) && (input.fields.slots === undefined || Object.keys(entry.slots).length === 0);
    const inverse = cheap
      ? [{ name: 'facet.setEntry' as const, input: { drawable: input.drawable, entry: input.entry, fields: structuredClone(Object.fromEntries(keys.map((k) => [k, prev[k]]))) } }]
      : entryInverse(input.drawable, input.entry, entry);
    Object.assign(entry, structuredClone(input.fields));
    checkPuppet(draft, ctx);
    return { result: {}, warnings: pivotWarnings(impostor, drawableIndex), inverse };
  },
};
